import { SecureRoute } from '@okta/okta-react';
import moment from 'moment';
import React, { FC, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Switch, useRouteMatch } from 'react-router';
import { PageLoading } from 'components/atoms/loading';
import {
  MyTimesheetPage,
  useTimesheetsForApprovers,
} from 'components/pages/timesheet';
import { useTimesheetExcludedUsers } from 'components/pages/timesheet-settings/excluded-users-settings';
import { useApprovalTabDate } from 'components/pages/timesheet/use-approval-tab-date';
import { getTimesheetMetaState } from 'data/timesheet-meta';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
// Importing from index file was causing some breaking changes, hence imported directly from the hook file
// eslint-disable-next-line import/no-internal-modules
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { localeData } from 'translations/en-moment';
import { TimesheetApprovalsRoutes } from './approvals';

export const TimesheetsRoute: FC = () => {
  const locale = 'en';

  useLayoutEffect(() => {
    moment.updateLocale(locale, { ...localeData, weekStart: 1 });

    return () => {
      moment.updateLocale(locale, { ...localeData, weekStart: 0 });
    };
  }, [locale]);

  const { approvalTabStartDate, setApprovalTabStartDate } =
    useApprovalTabDate();

  const { data: configuration } = useTimesheetConfiguration();
  const excludedUsers = useTimesheetExcludedUsers();
  const { isLoading: isUsersLoading } = useNativeActiveOrInvitedUsersQuery();
  const { data: timesheetMeta } = useSelector(getTimesheetMetaState);
  const {
    timesheets,
    isTimesheetLoading,
    pendingApprovalCount,
    hasApprovalTabPermission,
    onTimesheetStateChange,
    refreshData,
  } = useTimesheetsForApprovers(approvalTabStartDate);

  const { path } = useRouteMatch();

  return configuration == null ||
    excludedUsers[0] === 'loading' ||
    isUsersLoading ? (
    <PageLoading />
  ) : (
    <Switch>
      <SecureRoute path={`${path}/:week/approvals`}>
        <TimesheetApprovalsRoutes
          configuration={configuration}
          startDate={approvalTabStartDate}
          onStartDateChange={setApprovalTabStartDate}
          isTimesheetLoading={isTimesheetLoading}
          timesheets={timesheets}
          onTimesheetStateChange={onTimesheetStateChange}
          pendingApprovalCount={pendingApprovalCount}
          refreshData={refreshData}
          hasApprovalTabPermission={hasApprovalTabPermission}
        />
      </SecureRoute>
      {!timesheetMeta?.excludedFromTimesheet ? (
        <SecureRoute path={`${path}/:week/my-timesheet`}>
          <MyTimesheetPage
            configuration={configuration}
            pendingApprovalCount={pendingApprovalCount}
            hasApprovalTabPermission={hasApprovalTabPermission}
          />
        </SecureRoute>
      ) : null}
      <SecureRoute path="/">
        <Redirect
          to={
            timesheetMeta?.excludedFromTimesheet
              ? `${path}/this-week/approvals`
              : `${path}/this-week/my-timesheet`
          }
        />
      </SecureRoute>
    </Switch>
  );
};
